import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
    message: ""
  });

  const [consultationForm, setConsultationForm] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
    service_type: "",
    preferred_date: "",
    additional_info: ""
  });

  const [submitting, setSubmitting] = useState(false);

  const handleInquiryChange = (e) => {
    setInquiryForm({ ...inquiryForm, [e.target.name]: e.target.value });
  };

  const handleConsultationChange = (e) => {
    setConsultationForm({ ...consultationForm, [e.target.name]: e.target.value });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, inquiryForm);
      toast.success("Thank you! Your inquiry has been submitted successfully.");
      setInquiryForm({
        name: "",
        email: "",
        organization: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/consultations`, consultationForm);
      toast.success("Thank you! Your consultation request has been submitted. We'll contact you soon.");
      setConsultationForm({
        name: "",
        email: "",
        organization: "",
        phone: "",
        service_type: "",
        preferred_date: "",
        additional_info: ""
      });
    } catch (error) {
      console.error("Error submitting consultation:", error);
      toast.error("Failed to submit consultation request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 bg-[#FAF8F3]" data-testid="contact-page">
      {/* Header */}
      <section className="bg-primary-dark text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4 font-heading"
            data-testid="contact-heading"
          >
            Get In Touch
          </h1>
          <p className="text-lg text-gray-200 max-w-3xl">
            Ready to strengthen your community&apos;s health outcomes? We&apos;re here to help.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="text-primary mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-sm">Email</p>
                      <p className="text-sm text-neutral-gray">pcphservices@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="text-primary mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-sm">Phone</p>
                      <p className="text-sm text-neutral-gray">(505) 879-6673</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="text-primary mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-sm">Service Area</p>
                      <p className="text-sm text-neutral-gray">Serving Tribal Communities & Rural Regions Nationwide</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2 font-heading">Office Hours</h3>
                  <p className="text-sm text-neutral-gray mb-1">Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p className="text-sm text-neutral-gray">Saturday - Sunday: Closed</p>
                </CardContent>
              </Card>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  <Tabs defaultValue="inquiry" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="inquiry" data-testid="inquiry-tab">General Inquiry</TabsTrigger>
                      <TabsTrigger value="consultation" data-testid="consultation-tab">Book Consultation</TabsTrigger>
                    </TabsList>

                    {/* General Inquiry Form */}
                    <TabsContent value="inquiry">
                      <form onSubmit={handleInquirySubmit} className="space-y-4" data-testid="inquiry-form">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="inquiry-name">Name *</Label>
                            <Input
                              id="inquiry-name"
                              name="name"
                              value={inquiryForm.name}
                              onChange={handleInquiryChange}
                              required
                              data-testid="inquiry-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="inquiry-email">Email *</Label>
                            <Input
                              id="inquiry-email"
                              name="email"
                              type="email"
                              value={inquiryForm.email}
                              onChange={handleInquiryChange}
                              required
                              data-testid="inquiry-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="inquiry-organization">Organization</Label>
                            <Input
                              id="inquiry-organization"
                              name="organization"
                              value={inquiryForm.organization}
                              onChange={handleInquiryChange}
                              data-testid="inquiry-organization-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="inquiry-phone">Phone</Label>
                            <Input
                              id="inquiry-phone"
                              name="phone"
                              type="tel"
                              value={inquiryForm.phone}
                              onChange={handleInquiryChange}
                              data-testid="inquiry-phone-input"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="inquiry-message">Message *</Label>
                          <Textarea
                            id="inquiry-message"
                            name="message"
                            value={inquiryForm.message}
                            onChange={handleInquiryChange}
                            rows={6}
                            required
                            data-testid="inquiry-message-input"
                          />
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full bg-primary hover:bg-primary-dark text-white"
                          disabled={submitting}
                          data-testid="inquiry-submit-button"
                        >
                          {submitting ? "Sending..." : (
                            <>
                              <Send className="mr-2" size={16} />
                              Send Inquiry
                            </>
                          )}
                        </Button>
                      </form>
                    </TabsContent>

                    {/* Consultation Booking Form */}
                    <TabsContent value="consultation">
                      <form onSubmit={handleConsultationSubmit} className="space-y-4" data-testid="consultation-form">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="consultation-name">Name *</Label>
                            <Input
                              id="consultation-name"
                              name="name"
                              value={consultationForm.name}
                              onChange={handleConsultationChange}
                              required
                              data-testid="consultation-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="consultation-email">Email *</Label>
                            <Input
                              id="consultation-email"
                              name="email"
                              type="email"
                              value={consultationForm.email}
                              onChange={handleConsultationChange}
                              required
                              data-testid="consultation-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="consultation-organization">Organization *</Label>
                            <Input
                              id="consultation-organization"
                              name="organization"
                              value={consultationForm.organization}
                              onChange={handleConsultationChange}
                              required
                              data-testid="consultation-organization-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="consultation-phone">Phone *</Label>
                            <Input
                              id="consultation-phone"
                              name="phone"
                              type="tel"
                              value={consultationForm.phone}
                              onChange={handleConsultationChange}
                              required
                              data-testid="consultation-phone-input"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="consultation-service">Service of Interest *</Label>
                          <select
                            id="consultation-service"
                            name="service_type"
                            value={consultationForm.service_type}
                            onChange={handleConsultationChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[primary]"
                            data-testid="consultation-service-select"
                          >
                            <option value="">Select a service</option>
                            <option value="Evaluation & Training">Evaluation & Training</option>
                            <option value="Custom Dashboards & Data Solutions">Custom Dashboards & Data Solutions</option>
                            <option value="Outreach & Community Health Services">Outreach & Community Health Services</option>
                            <option value="Educational Classes & Health Promotion">Educational Classes & Health Promotion</option>
                            <option value="Community Health Needs Assessments">Community Health Needs Assessments (CHNAs)</option>
                            <option value="Community Data & Analytics">Community Data & Analytics</option>
                            <option value="Focus Groups & Community Engagement">Focus Groups & Community Engagement</option>
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="consultation-date">Preferred Date *</Label>
                          <Input
                            id="consultation-date"
                            name="preferred_date"
                            type="date"
                            value={consultationForm.preferred_date}
                            onChange={handleConsultationChange}
                            required
                            data-testid="consultation-date-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="consultation-info">Additional Information</Label>
                          <Textarea
                            id="consultation-info"
                            name="additional_info"
                            value={consultationForm.additional_info}
                            onChange={handleConsultationChange}
                            rows={4}
                            data-testid="consultation-info-input"
                          />
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full bg-primary hover:bg-primary-dark text-white"
                          disabled={submitting}
                          data-testid="consultation-submit-button"
                        >
                          {submitting ? "Submitting..." : (
                            <>
                              <Send className="mr-2" size={16} />
                              Request Consultation
                            </>
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
